import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Save, Building2, Palette, FileImage, MapPin, Phone,
  CreditCard, QrCode, Loader2, Hash, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { ATHLETE_NUMBER_DIGIT_OPTIONS } from "@/lib/athlete-number";
import {
  getMyTrainingCenter, upsertTrainingCenter, type TrainingCenterInput,
} from "@/lib/api/training-center.functions";
import { verifyAdminPin } from "@/lib/api/staff-pin.functions";
import { supabase } from "@/integrations/supabase/client";
import { ImageUploadField, GalleryUploadField } from "@/components/training-center/MediaUpload";


export const Route = createFileRoute("/_authenticated/m/dados-ct")({
  component: TrainingCenterPage,
});

const empty: TrainingCenterInput = {
  status: "ATIVO",
  primary_color: "#1e3eff",
  announcement: "",
  gallery_urls: [],
  name: "",
  manager_name: "",
  entity_type: "pessoa_fisica",
  cpf: "",
  cnpj: "",
  corporate_name: "",
  logo_url: "",
  manager_photo: "",
  center_photo: "",
  history: "",
  cep: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  whatsapp: "",
  phone: "",
  email: "",
  mp_access_token: "",
  mp_public_key: "",
  pix_beneficiary: "",
  pix_key: "",
  pix_qr_code: "",
  allow_repeated_athlete_numbers: false,
  athlete_number_digits: 3,
};

function TrainingCenterPage() {
  const navigate = useNavigate();
  const fetch = useServerFn(getMyTrainingCenter);
  const save = useServerFn(upsertTrainingCenter);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tcId, setTcId] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [form, setForm] = useState<TrainingCenterInput>(empty);
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [pin, setPin] = useState("");
  const checkPin = useServerFn(verifyAdminPin);

  const setField = <K extends keyof TrainingCenterInput>(k: K, v: TrainingCenterInput[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const onUnlock = async () => {
    setUnlocking(true);
    try {
      await checkPin({ data: { pin } });
      setUnlocked(true);
      setPin("");
    } catch (e: any) {
      toast.error(e?.message || "PIN incorreto");
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { trainingCenter } = await fetch();
        // Dados da conta logada para pré-preencher responsável e e-mail.
        const { data: auth } = await supabase.auth.getUser();
        const meta = (auth?.user?.user_metadata ?? {}) as any;
        const loginName: string = meta.name || meta.full_name || "";
        const loginEmail: string = auth?.user?.email ?? "";

        if (trainingCenter) {
          setTcId(trainingCenter.id);
          setCreatedAt(trainingCenter.created_at);
          const loaded = {
            ...empty,
            ...Object.fromEntries(
              Object.entries(trainingCenter).map(([k, v]) => [k, v ?? (Array.isArray(v) ? [] : "")]),
            ),
          } as TrainingCenterInput;
          setForm({
            ...loaded,
            status: "ATIVO",
            manager_name: loaded.manager_name || loginName,
            email: loaded.email || loginEmail,
          });
        } else {
          setForm((p) => ({
            ...p,
            status: "ATIVO",
            manager_name: p.manager_name || loginName,
            email: p.email || loginEmail,
          }));
        }
      } catch (e: any) {
        toast.error(e?.message || "Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fetchCep = async (cep: string) => {
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    try {
      const res = await window.fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();
      if (data.erro) return;
      setForm((p) => ({
        ...p,
        street: data.logradouro || p.street,
        neighborhood: data.bairro || p.neighborhood,
        city: data.localidade || p.city,
        state: data.uf || p.state,
      }));
    } catch {}
  };

  const validate = (): string | null => {
    const req: Array<[unknown, string]> = [
      [form.name, "Nome do Centro de Treinamento"],
      [form.manager_name, "Nome do Responsável"],
      [form.entity_type === "pessoa_fisica" ? form.cpf : form.cnpj,
        form.entity_type === "pessoa_fisica" ? "CPF" : "CNPJ"],
      [form.cep, "CEP"],
      [form.street, "Rua"],
      [form.number, "Número"],
      [form.neighborhood, "Bairro"],
      [form.city, "Cidade"],
      [form.state, "UF"],
      [form.email, "E-mail"],
      [form.whatsapp, "WhatsApp"],
    ];
    const missing = req.filter(([v]) => !String(v ?? "").trim()).map(([, l]) => l);
    return missing.length ? `Preencha: ${missing.join(", ")}.` : null;
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    try {
      const res = await save({ data: { ...form, status: "ATIVO" } });
      setTcId(res.id);
      toast.success("Centro de Treinamento salvo!");
      if (!createdAt) setCreatedAt(new Date().toISOString());
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/dashboard" })}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {form.name || "Cadastro do Centro de Treinamento"}
            </h1>
            <p className="text-sm text-muted-foreground">Gerencie o CT, professores e alunos associados.</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} size="lg">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Salvando..." : "Salvar"}
        </Button>
      </div>

      {form.name && (
        <Card className="bg-muted/30 border-dashed">
          <CardContent className="p-4">
            <p className="font-semibold">{form.name}</p>
            <p className="text-sm text-primary">{form.history || "Sem histórico cadastrado"}</p>
          </CardContent>
        </Card>
      )}

      {/* Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4" /> Status do Cadastro
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2 sm:col-span-3">
            <Label>ID do Centro de Treinamento (Identificador Único)</Label>
            <Input value={tcId ?? "—"} disabled className="font-mono text-xs" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Data de Cadastro</Label>
            <Input value={createdAt ? new Date(createdAt).toLocaleDateString("pt-BR") : "—"} disabled />
          </div>
          <div className="space-y-2">
            <Label>Situação</Label>
            <Input value="ATIVO" disabled readOnly />
          </div>

        </CardContent>
      </Card>

      {/* Personalização */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="h-4 w-4" /> Personalização e Comunicação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Cor Principal do Sistema</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.primary_color || "#1e3eff"}
                onChange={(e) => setField("primary_color", e.target.value)}
                className="h-10 w-16 rounded border bg-background cursor-pointer"
              />
              <span className="text-sm text-primary">Escolha a cor que combina com sua logomarca.</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Comunicado em Destaque</Label>
            <Textarea
              rows={3}
              value={form.announcement ?? ""}
              onChange={(e) => setField("announcement", e.target.value)}
              placeholder="Aviso importante que ficará visível para todos os usuários logo na tela inicial..."
            />
          </div>
          <div className="space-y-2">
            <Label>Galeria de Mídia do CT (Dia a Dia)</Label>
            <GalleryUploadField
              tcId={tcId}
              value={form.gallery_urls ?? []}
              onChange={(paths) => setField("gallery_urls", paths)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Numeração dos Atletas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Hash className="h-4 w-4" /> Numeração dos Atletas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]"
              checked={!!form.allow_repeated_athlete_numbers}
              onChange={(e) => setField("allow_repeated_athlete_numbers", e.target.checked)}
            />
            <span className="text-sm">
              Permitir números repetidos
              <span className="block text-xs text-muted-foreground">
                Desmarcado: cada número só pode ser usado por um atleta do CT.
              </span>
            </span>
          </label>
          <div className="space-y-2 max-w-xs">
            <Label>Quantidade máxima de algarismos</Label>
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={String(form.athlete_number_digits ?? 3)}
              onChange={(e) => setField("athlete_number_digits", Number(e.target.value) as any)}
            >
              {ATHLETE_NUMBER_DIGIT_OPTIONS.map((d) => (
                <option key={d} value={d}>até {d} algarismos</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              O número pode ter de 1 até a quantidade escolhida de algarismos. Ele é único e
              compartilhado entre Cadastro do Aluno, Uniforme e Skin.
            </p>

          </div>
        </CardContent>
      </Card>



      {/* Dados Gerais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4" /> Dados Gerais
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Nome do Centro de Treinamento *</Label>
            <Input required value={form.name} onChange={(e) => setField("name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Nome do Responsável *</Label>
            <Input required value={form.manager_name} onChange={(e) => setField("manager_name", e.target.value)} />
            <p className="text-xs text-muted-foreground">Preenchido com o nome do seu login. Você pode alterar.</p>
          </div>
          <div className="space-y-2">
            <Label>Tipo de Cadastro</Label>
            <RadioGroup
              value={form.entity_type}
              onValueChange={(v: any) => setField("entity_type", v)}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="pessoa_fisica" id="pf" />
                <Label htmlFor="pf" className="cursor-pointer">Pessoa Física</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="pessoa_juridica" id="pj" />
                <Label htmlFor="pj" className="cursor-pointer">Pessoa Jurídica</Label>
              </div>
            </RadioGroup>
          </div>
          {form.entity_type === "pessoa_fisica" ? (
            <div className="space-y-2">
              <Label>CPF *</Label>
              <Input required value={form.cpf ?? ""} onChange={(e) => setField("cpf", e.target.value)} placeholder="000.000.000-00" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CNPJ *</Label>
                <Input required value={form.cnpj ?? ""} onChange={(e) => setField("cnpj", e.target.value)} placeholder="00.000.000/0000-00" />
              </div>
              <div className="space-y-2">
                <Label>Razão Social</Label>
                <Input value={form.corporate_name ?? ""} onChange={(e) => setField("corporate_name", e.target.value)} />
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Fotos & Histórico */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileImage className="h-4 w-4" /> Fotos e Histórico
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Logomarca</Label>
              <ImageUploadField
                tcId={tcId}
                value={form.logo_url}
                onChange={(p) => setField("logo_url", p)}
                label="Enviar logomarca"
              />
            </div>
            <div className="space-y-2">
              <Label>Foto do Responsável</Label>
              <ImageUploadField
                tcId={tcId}
                value={form.manager_photo}
                onChange={(p) => setField("manager_photo", p)}
                label="Enviar foto"
              />
            </div>
            <div className="space-y-2">
              <Label>Foto do Centro de Treinamento</Label>
              <ImageUploadField
                tcId={tcId}
                value={form.center_photo}
                onChange={(p) => setField("center_photo", p)}
                label="Enviar foto"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Breve Histórico de Apresentação</Label>
            <Textarea rows={4} value={form.history ?? ""} onChange={(e) => setField("history", e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {/* Endereço */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4" /> Endereço
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>CEP *</Label>
              <Input
                required
                value={form.cep ?? ""}
                onChange={(e) => setField("cep", e.target.value)}
                onBlur={(e) => fetchCep(e.target.value)}
                placeholder="00000-000"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Rua *</Label>
              <Input required value={form.street ?? ""} onChange={(e) => setField("street", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Número *</Label>
              <Input required value={form.number ?? ""} onChange={(e) => setField("number", e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Complemento (opcional)</Label>
              <Input value={form.complement ?? ""} onChange={(e) => setField("complement", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Bairro *</Label>
              <Input required value={form.neighborhood ?? ""} onChange={(e) => setField("neighborhood", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Cidade *</Label>
              <Input required value={form.city ?? ""} onChange={(e) => setField("city", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>UF *</Label>
              <Input required maxLength={2} value={form.state ?? ""} onChange={(e) => setField("state", e.target.value.toUpperCase())} />
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Contato */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-4 w-4" /> Contato
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>WhatsApp *</Label>
            <Input required value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} placeholder="(00) 00000-0000" />
          </div>
          <div className="space-y-2">
            <Label>Telefone Fixo (Opcional)</Label>
            <Input value={form.phone ?? ""} onChange={(e) => setField("phone", e.target.value)} placeholder="(00) 0000-0000" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>E-mail *</Label>
            <Input required type="email" value={form.email ?? ""} onChange={(e) => setField("email", e.target.value)} />
            <p className="text-xs text-muted-foreground">Preenchido com o e-mail do seu login. Você pode alterar.</p>
          </div>

        </CardContent>
      </Card>

      {/* MP & PIX — área restrita ao PIN do administrador */}
      {!unlocked ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Lock className="h-4 w-4" /> Configurações de Recebimento (área restrita)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Integração Mercado Pago e Recebimento Pix Direto. Informe o PIN de administrador do
              Centro de Treinamento para configurar.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                className="max-w-[180px]"
                placeholder="PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              />
              <Button variant="secondary" onClick={onUnlock} disabled={unlocking}>
                {unlocking ? "Validando..." : "Desbloquear"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
      <Accordion type="multiple" className="space-y-3">
        <AccordionItem value="mp" className="border rounded-lg bg-card px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="font-medium">Integração Mercado Pago</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <p className="text-xs text-muted-foreground">Configure suas credenciais para receber pagamentos online (PIX e Cartão de Crédito).</p>
            <Badge variant="secondary" className="text-xs">Configurado pelo FutBux</Badge>
            <div className="space-y-2">
              <Label>Access Token (Token de Acesso)</Label>
              <Input
                type="password"
                value={form.mp_access_token ?? ""}
                onChange={(e) => setField("mp_access_token", e.target.value)}
                placeholder="APP_USR-..."
              />
              <p className="text-xs text-muted-foreground">Token de acesso para criação de pagamentos PIX e processamento.</p>
            </div>
            <div className="space-y-2">
              <Label>Public Key (Chave Pública)</Label>
              <Input
                value={form.mp_public_key ?? ""}
                onChange={(e) => setField("mp_public_key", e.target.value)}
                placeholder="APP_USR-..."
              />
              <p className="text-xs text-muted-foreground">Chave pública utilizada para o checkout transparente de cartão de crédito.</p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="pix" className="border rounded-lg bg-card px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              <span className="font-medium">Recebimento PIX Direto</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <p className="text-xs text-muted-foreground">Se preferir não usar o Mercado Pago, configure aqui a sua chave PIX direta.</p>
            <Badge variant="secondary" className="text-xs">Configurado pelo FutBux</Badge>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome do Beneficiário</Label>
                <Input value={form.pix_beneficiary ?? ""} onChange={(e) => setField("pix_beneficiary", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Chave PIX</Label>
                <Input value={form.pix_key ?? ""} onChange={(e) => setField("pix_key", e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>QR Code do PIX</Label>
              <ImageUploadField
                tcId={tcId}
                value={form.pix_qr_code}
                onChange={(p) => setField("pix_qr_code", p)}
                label="Enviar QR Code"
                previewHeight="h-40"
                className="max-w-xs"
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      )}





      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Salvando..." : "Salvar Centro de Treinamento"}
        </Button>
      </div>
    </div>
  );
}
